/**
 * Insurance search ranking for the selection search box.
 *
 * Mirrors `physiofit_app/app/features/onboarding/utils/searchInsurances.ts` (identical in physiofit_app_V2). Keep this
 * copy identical to it apart from formatting and doc comments, so the app's jest table covers it too; this repo has
 * no test runner.
 */

import type { Insurance } from '@physiofit-digital/physiofit_types';

export type SearchableInsurance = Pick<
  Insurance,
  'name' | 'label' | 'shortName' | 'searchAliases' | 'privateInsurance' | 'filterIndex'
>;

const CATCH_ALL_PRIVATE_NAME = 'Private Versicherung';
const MAX_ENTRIES_FOR_SPECIFIC_WORD = 3;
// Dropped whenever the query has other words, so "Landesamt für Besoldung" does not match an insurer through "für",
// and "bei der TK versichert" does not list every "…versicherung". Written as `fold` returns them.
const FILLER_WORDS = new Set(
  (
    'der die das den dem des ein eine einer und oder fur von vom bei beim mit ' +
    'im in am an auf zu zur zum ich bin mein meine meiner mich mir bitte versichert'
  ).split(' '),
);

type IndexedInsurance<T extends SearchableInsurance> = {
  insurance: T;
  words: string[];
  compacts: string[];
};

type ScoredInsurance<T extends SearchableInsurance> = IndexedInsurance<T> & {
  exact: number;
  matchedTokenCount: number;
  score: number;
};

function fold(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Private intent hides every other insurer, so a partial "privat" only counts from four letters ("priv") on.
function isPrivateTerm(token: string): boolean {
  return token === 'pkv' || token.startsWith('privat') || (token.length >= 4 && 'privat'.startsWith(token));
}

// The search runs on every keystroke, so each catalogue row and each catalogue word is folded and encoded once, not
// per keystroke.
const searchTextCache = new WeakMap<SearchableInsurance, { words: string[]; compacts: string[] }>();
const wordSoundCodeCache = new Map<string, string>();

function indexInsurance<T extends SearchableInsurance>(insurance: T): IndexedInsurance<T> {
  const cached = searchTextCache.get(insurance);
  if (cached) return { insurance, ...cached };
  const fields = [insurance.name, insurance.label, insurance.shortName, ...(insurance.searchAliases ?? [])]
    .filter((field): field is string => typeof field === 'string' && field.trim().length > 0)
    .map(fold)
    .filter(Boolean);
  const words = [...new Set(fields.flatMap((field) => field.split(' ')))];
  const compacts = [...new Set(fields.map((field) => field.replace(/ /g, '')))];
  searchTextCache.set(insurance, { words, compacts });
  return { insurance, words, compacts };
}

function strictTier<T extends SearchableInsurance>(entry: IndexedInsurance<T>, token: string): number {
  if (entry.words.includes(token)) return 3;
  if (entry.words.some((word) => word.startsWith(token))) return 2;
  if (token.length >= 3 && entry.compacts.some((compact) => compact.includes(token))) return 1;
  return 0;
}

function editDistanceWithin(left: string, right: string, max: number): number {
  if (Math.abs(left.length - right.length) > max) return max + 1;
  let previousRow = Array.from({ length: right.length + 1 }, (_, column) => column);
  for (let row = 1; row <= left.length; row++) {
    const currentRow = [row];
    let rowMin = row;
    for (let column = 1; column <= right.length; column++) {
      currentRow[column] = Math.min(
        previousRow[column] + 1,
        currentRow[column - 1] + 1,
        previousRow[column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1),
      );
      rowMin = Math.min(rowMin, currentRow[column]);
    }
    if (rowMin > max) return max + 1;
    previousRow = currentRow;
  }
  return previousRow[right.length];
}

function maxTypos(token: string): number {
  return token.length >= 8 ? 2 : token.length >= 4 ? 1 : 0;
}

function prefixEditDistanceWithin(token: string, word: string, max: number): number {
  let best = max + 1;
  const shortest = Math.max(1, token.length - max);
  const longest = Math.min(word.length, token.length + max);
  for (let length = shortest; length <= longest; length++) {
    best = Math.min(best, editDistanceWithin(token, word.slice(0, length), max));
  }
  return best;
}

function lenientTier<T extends SearchableInsurance>(
  entry: IndexedInsurance<T>,
  token: string,
  specificWords: Set<string>,
): number {
  // Compounds like "bundespolizei" must match "polizei", but a generic part like "versicherung" must not. A compound
  // hit counts no more than a typo: in "pfrrverein" the inner "verein" is part of the typo, not the insurer.
  if (entry.words.some((word) => word.length >= 5 && specificWords.has(word) && token.includes(word))) return 2;
  const allowedTypos = maxTypos(token);
  if (
    allowedTypos &&
    [...entry.words, ...entry.compacts].some((word) => editDistanceWithin(token, word, allowedTypos) <= allowedTypos)
  ) {
    return 2;
  }
  // A typo in a half-typed word ("tecn" for "techniker") ranks below a typo in a whole word, so it never pushes that
  // word's insurer down.
  if (
    allowedTypos &&
    entry.words.some(
      (word) => word.length > token.length && prefixEditDistanceWithin(token, word, allowedTypos) <= allowedTypos,
    )
  ) {
    return 1;
  }
  return 0;
}

// Kölner Phonetik, the standard German sound code: "tekni" and "techniker" both start with 246.
function colognePhoneticDigit(letter: string, previous: string, next: string, isFirst: boolean): string {
  const nextIs = (letters: string) => next !== '' && letters.includes(next);
  const previousIs = (letters: string) => previous !== '' && letters.includes(previous);
  if ('aeijouy'.includes(letter)) return '0';
  if (letter === 'b') return '1';
  if (letter === 'p') return nextIs('h') ? '3' : '1';
  if (letter === 'd' || letter === 't') return nextIs('csz') ? '8' : '2';
  if ('fvw'.includes(letter)) return '3';
  if ('gkq'.includes(letter)) return '4';
  if (letter === 'c') {
    if (isFirst) return nextIs('ahkloqrux') ? '4' : '8';
    return nextIs('ahkoqux') && !previousIs('sz') ? '4' : '8';
  }
  if (letter === 'x') return previousIs('ckq') ? '8' : '48';
  if (letter === 'l') return '5';
  if (letter === 'm' || letter === 'n') return '6';
  if (letter === 'r') return '7';
  if (letter === 's' || letter === 'z') return '8';
  return '';
}

function colognePhonetic(word: string): string {
  const letters = word.replace(/[^a-z]/g, '');
  const digits = [...letters]
    .map((letter, index) =>
      colognePhoneticDigit(letter, letters[index - 1] ?? '', letters[index + 1] ?? '', index === 0),
    )
    .join('');
  const collapsed = digits.replace(/(\d)\1+/g, '$1');
  return collapsed.slice(0, 1) + collapsed.slice(1).replace(/0/g, '');
}

function soundAlikeTier<T extends SearchableInsurance>(entry: IndexedInsurance<T>, token: string): number {
  if (token.length < 4) return 0;
  const code = colognePhonetic(token);
  return code.length >= 3 && entry.words.some((word) => wordSoundCode(word).startsWith(code)) ? 1 : 0;
}

function wordSoundCode(word: string): string {
  let code = wordSoundCodeCache.get(word);
  if (code === undefined) {
    code = colognePhonetic(word);
    wordSoundCodeCache.set(word, code);
  }
  return code;
}

function byTieBreak<T extends SearchableInsurance>(a: IndexedInsurance<T>, b: IndexedInsurance<T>): number {
  // Statutory before private, so a namesake such as "Debeka" lists the statutory entry first. Among private entries the
  // catch-all wins a tie, so aliases shared by many private insurers ("Beihilfe", "Beamte") list it first.
  return (
    (b.insurance.filterIndex ?? 0) - (a.insurance.filterIndex ?? 0) ||
    Number(a.insurance.privateInsurance) - Number(b.insurance.privateInsurance) ||
    Number(b.insurance.name === CATCH_ALL_PRIVATE_NAME) - Number(a.insurance.name === CATCH_ALL_PRIVATE_NAME) ||
    a.insurance.name.localeCompare(b.insurance.name, 'de')
  );
}

function browseOrder<T extends SearchableInsurance>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    if (a.name === CATCH_ALL_PRIVATE_NAME) return -1;
    if (b.name === CATCH_ALL_PRIVATE_NAME) return 1;
    return Number(a.privateInsurance) - Number(b.privateInsurance) || a.name.localeCompare(b.name, 'de');
  });
}

function specificWordsOf<T extends SearchableInsurance>(entries: IndexedInsurance<T>[]): Set<string> {
  const entryCountPerWord = new Map<string, number>();
  for (const entry of entries) {
    for (const word of entry.words) {
      entryCountPerWord.set(word, (entryCountPerWord.get(word) ?? 0) + 1);
    }
  }
  return new Set(
    [...entryCountPerWord].filter(([, count]) => count <= MAX_ENTRIES_FOR_SPECIFIC_WORD).map(([word]) => word),
  );
}

// A word no entry matches strictly is most likely a typo. It gets the typo tiers instead, or, when no entry has a typo
// match, the sound-alike tier, which is the noisiest. So "Debka BKK" finds Debeka BKK instead of every BKK, and
// "Tekni" still finds Techniker.
function strictTierWithTypoFallback<T extends SearchableInsurance>(
  entries: IndexedInsurance<T>[],
  tokens: string[],
  specificWords: Set<string>,
): (entry: IndexedInsurance<T>, token: string) => number {
  const fallbacks = new Map<string, (entry: IndexedInsurance<T>) => number>();
  const unmatchedTokens = tokens.filter((token) => !entries.some((entry) => strictTier(entry, token) > 0));
  for (const token of unmatchedTokens) {
    const typo = (entry: IndexedInsurance<T>) => lenientTier(entry, token, specificWords);
    const soundAlike = (entry: IndexedInsurance<T>) => soundAlikeTier(entry, token);
    fallbacks.set(token, entries.some((entry) => typo(entry) > 0) ? typo : soundAlike);
  }
  return (entry, token) => strictTier(entry, token) || (fallbacks.get(token)?.(entry) ?? 0);
}

// Only entries matching the most query words stay, so "AOK Bayern" does not list every AOK. They are ordered by an
// exact name or alias hit, then by tiers weighted by rarity ("krankenkasse" counts less than "debeka"), then by the
// tie-break. To lift an insurer for a query, add that query as an Airtable alias; `filterIndex` only breaks ties.
function rank<T extends SearchableInsurance>(
  entries: IndexedInsurance<T>[],
  tokens: string[],
  tierOf: (entry: IndexedInsurance<T>, token: string) => number,
  compactQuery?: string,
): ScoredInsurance<T>[] {
  const tiers = entries.map((entry) => tokens.map((token) => tierOf(entry, token)));
  const entryCountPerToken = tokens.map((_, tokenIndex) => tiers.filter((row) => row[tokenIndex] > 0).length);
  const weight = entryCountPerToken.map((matchingEntryCount) =>
    matchingEntryCount ? Math.log(1 + entries.length / matchingEntryCount) : 0,
  );
  const scored = entries.map((entry, entryIndex) => ({
    ...entry,
    exact: compactQuery && entry.compacts.includes(compactQuery) ? 1 : 0,
    matchedTokenCount: tiers[entryIndex].filter((tier) => tier > 0).length,
    score: tiers[entryIndex].reduce((sum, tier, tokenIndex) => sum + tier * weight[tokenIndex], 0),
  }));
  const mostMatchedTokens = Math.max(0, ...scored.map((entry) => entry.matchedTokenCount));
  return scored
    .filter((entry) => mostMatchedTokens > 0 && entry.matchedTokenCount === mostMatchedTokens)
    .sort((a, b) => b.exact - a.exact || b.score - a.score || byTieBreak(a, b));
}

/**
 * Ranks the insurer catalogue against the patient's search input. Private intent ("privat", "PKV") lists
 * `Private Versicherung` first and narrows to the insurers flagged private; every other query is ranked word by word.
 *
 * @param {Array} insurances Insurer catalog rows to search.
 * @param {string} query Free-text search input.
 * @return {Array} Matching rows, most relevant first. An empty query returns every row, `Private Versicherung`
 *   first, then statutory before private insurers, each by name.
 */
export function searchInsurances<T extends SearchableInsurance>(insurances: T[], query: string): T[] {
  const words = [...new Set(fold(query).split(' ').filter(Boolean))];
  const meaningfulWords = words.filter((word) => !FILLER_WORDS.has(word));
  const tokens = meaningfulWords.length ? meaningfulWords : words;
  if (!tokens.length) return browseOrder(insurances);
  const entries = insurances.map((insurance) => indexInsurance(insurance));
  const specificWords = specificWordsOf(entries);

  if (tokens.some(isPrivateTerm)) {
    const remainingTokens = tokens.filter((token) => !isPrivateTerm(token));
    // Flag, not wording: patients look for Bundeswehr, Polizei and Selbstzahler under "privat" too, although their
    // names never say so. The flag also brings in Asylbewerber.
    const scope = entries.filter((entry) => entry.insurance.privateInsurance);
    const catchAll = scope.find((entry) => entry.insurance.name === CATCH_ALL_PRIVATE_NAME);
    const others = scope.filter((entry) => entry !== catchAll);
    const matched = remainingTokens.length
      ? rank(others, remainingTokens, strictTierWithTypoFallback(others, remainingTokens, specificWords))
      : [];
    const restOfScope = others
      .filter((entry) => !matched.some((match) => match.insurance === entry.insurance))
      .sort(byTieBreak);
    return [...(catchAll ? [catchAll] : []), ...matched, ...restOfScope].map((entry) => entry.insurance);
  }

  return rank(entries, tokens, strictTierWithTypoFallback(entries, tokens, specificWords), tokens.join('')).map(
    (entry) => entry.insurance,
  );
}

/**
 * Row label for the insurer list, repeated in the selection field until the pick is confirmed. Only these labels show
 * the "(privat)" tag: catalogue names reach certificates and patient data, so they stay without it. The "PKV" alias,
 * not the flag, picks the private insurers: the flag also marks Bundeswehr, Polizei, Selbstzahler and Asylbewerber.
 *
 * @param {Object} insurance Insurer catalog row.
 * @return {string} The name, with "(privat)" appended for private insurers whose name does not already say so.
 */
export function insuranceListName(insurance: SearchableInsurance): string {
  const name = insurance.name.trim();
  const isPrivateInsurer =
    insurance.privateInsurance && (insurance.searchAliases ?? []).some((alias) => fold(alias) === 'pkv');
  const nameSaysPrivate = fold(name).split(' ').some(isPrivateTerm);
  return isPrivateInsurer && !nameSaysPrivate ? `${name} (privat)` : name;
}
